import { useRef, useSyncExternalStore } from 'react'
import type { TouchEvent } from 'react'

import type { CalendarEntry } from '~/api/types'

import { EventCard } from '~/components/EventCard'

import { mondayOf } from './address'

import s from './WeekView.module.css'
import { inNightOrder } from './views'

/**
 * The week, ported from `_calendar_page.py`'s `renderWeek` and the helpers under it.
 *
 * Seven columns, one per night, each an ordered run of what is on. It used to be a
 * timetable: six rows, 18:00 to 23:00, every block filed into the hour it started. That is
 * the shape a diary needs, where the question is what overlaps what — and it is not the
 * question anybody brings to a gig calendar. Doors are at eight. The hour rail cost a column
 * of width and five sixths of the height to say so, and a 21:00 set and a 22:00 set were
 * drawn a hundred pixels apart as though the difference mattered.
 *
 * So the hours are gone and the order they implied is kept: earliest first, and anything
 * with no published time last. The week has far more room per night than the month does,
 * which is what pays for the artwork — the same thumbnails the month grid carries on its
 * chips, at a size you can actually recognise a poster from.
 */

/** The signed-in viewer. Local by request: this view needs only the presence of one. */
export interface Me {
  id: number
  name: string
  username: string
  isEditor: boolean
}

/**
 * Which week to open on. Switching view is not the same as navigating: a month grid open on
 * October that jumps to this week has thrown away where you were, and the arrows are then
 * the only way back to it. The day you had selected wins, then the first gig in the month on
 * screen, and only a cold start falls through to whatever the week view finds.
 *
 * Exported rather than used here, because the state it reads — the selected day, the month
 * on screen — belongs to the calendar around this view and is handed back as `weekStart`.
 */
export function weekAnchor(
  entries: readonly CalendarEntry[],
  { selDay, month }: { selDay: string | null; month: string | null },
): string | null {
  if (selDay) return mondayOf(selDay)
  if (!month) return null
  const first = [...new Set(entries.map(e => e.iso))]
    .sort()
    .find(iso => iso.slice(0, 7) === month)
  return mondayOf(first || `${month}-01`)
}

/** The seven nights of a week, from its Monday. Midday UTC, so no zone can shift the date. */
export function weekOf(startIso: string): string[] {
  const out: string[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(`${startIso}T12:00:00Z`)
    d.setUTCDate(d.getUTCDate() + i)
    out.push(d.toISOString().slice(0, 10))
  }
  return out
}

/** The Monday `by` days from this one. The scroll and the re-render are the caller's. */
export function shiftWeek(weekStart: string, by: number): string {
  const d = new Date(`${weekStart}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + by)
  return d.toISOString().slice(0, 10)
}

/**
 * A cap and then a count that opens the day. A night is not a fixed size — an unfiltered
 * Saturday holds twenty gigs — and seven unbounded columns means six of them are white space
 * next to one that runs off the bottom.
 */
const PER_CELL = 4

export function perCell(phone: boolean): number {
  return phone ? PER_CELL : PER_CELL + 2
}

/**
 * 720px, asked rather than assumed: the phone is a different design, not a narrower one.
 * Read live, so that rotating a tablet is a re-render rather than a reload — which is why
 * this is a subscription and not a one-shot read in an effect.
 */
const PHONE = '(max-width:720px)'

function subscribePhone(onChange: () => void): () => void {
  const mql = window.matchMedia(PHONE)
  mql.addEventListener('change', onChange)
  return () => mql.removeEventListener('change', onChange)
}

function usePhone(): boolean {
  return useSyncExternalStore(subscribePhone, () => window.matchMedia(PHONE).matches)
}

/**
 * A gig is on the list two ways that are not a personal save: the editor added it, or a
 * starred act headlines it. Both are `picked`, and the mark is on for both. Drawing the
 * highlight from `liked` alone put Wilco at Hammersmith on the list with no colour on it,
 * between two gigs that had both.
 */



/**
 * Today, in London. The Python reads `DATA.today` off the payload, which is the right source
 * — the server formats every date on an entry in Europe/London precisely so the SPA does not
 * keep a second timezone rule. This view has no prop to receive it on, so it derives the one
 * date it needs and no others. A `today` prop would retire this; see the port report.
 */

/** A vertical scroll that drifts sideways is not a swipe, and neither is a tap. */
const SWIPE_MIN_PX = 55
const SWIPE_SLOPE = 1.6

export interface WeekViewProps {
  /**
   * Today, as the server saw it — `YYYY-MM-DD`, London.
   *
   * This was derived here from the browser clock with a `Europe/London` formatter, in this
   * view and in two others. That is right nearly always and wrong exactly when it matters: a
   * machine a few minutes off, or a reader looking across London's midnight, gets a different
   * today from the one the calendar was built with. The night rule already cuts the day at
   * 05:00; "nearly right" is not a property this page can afford. The payload has carried it
   * all along.
   */
  today: string
  /** Already filtered to the view and the venue by the calendar around this one. */
  entries: CalendarEntry[]
  /**
   * What each night would hold without the payload's per-night cap — `{iso: total}`, the
   * same key from the same payload as `MonthGridProps.nights`.
   *
   * The count under a column is this view's only way of saying a night has more than it is
   * showing, and the cap is what takes that ability away: a column has room for more than
   * the wire sends, so counting off the rows in hand says nothing at all on the nights with
   * most behind them. This view's room being *deeper* than the cap is why it needs the count
   * more than the month does, not less.
   *
   * Optional, and left out wherever this grid is not drawing the population the numbers
   * count — see `MonthGridProps.nights` for which tabs those are.
   */
  nights?: Record<string, number> | undefined
  /** The Monday of the week on screen, `YYYY-MM-DD`. */
  weekStart: string
  /** The day header is a way into the day, not a label on it — and so is the overflow count. */
  onDay(iso: string): void
  /** Swipe for the next week. `+7` or `-7`; the caller owns `weekStart`. */
  onShift(days: number): void
  /**
   * Whether a night's rows are still on their way — see `MonthGridProps.pending`, which this
   * is the same answer from the same calendar. Left out, every night is taken as arrived.
   */
  pending?: ((night: string) => boolean) | undefined
}

export function WeekView({
  entries,
  nights: onNights,
  weekStart,
  today,
  onDay,
  onShift,
  pending,
}: WeekViewProps) {
  const phone = usePhone()
  const room = perCell(phone)

  const cols = weekOf(weekStart)
  const nights = new Map<string, CalendarEntry[]>()
  for (const e of entries) {
    if (!cols.includes(e.iso)) continue
    const list = nights.get(e.iso)
    if (list) list.push(e)
    else nights.set(e.iso, [e])
  }
  // What you have decided about it, then the clock — the same order the month cell and the
  // list row use, from the same function.
  for (const [iso, list] of nights) nights.set(iso, inNightOrder(list))

  /*
   * Swipe for the next week — the gesture the month grid cannot offer, because a month is
   * already seven columns of horizontal target and a sideways drag there is ambiguous.
   */
  const from = useRef<{ x: number; y: number } | null>(null)

  function touchStart(ev: TouchEvent<HTMLDivElement>) {
    const touch = ev.touches[0]
    if (!touch) return
    from.current = { x: touch.clientX, y: touch.clientY }
  }

  function touchEnd(ev: TouchEvent<HTMLDivElement>) {
    const start = from.current
    const touch = ev.changedTouches[0]
    // Cleared before the thresholds, as in the Python: a drag that fails them still ends
    // the gesture, or the next tap is measured from wherever this one began.
    from.current = null
    if (!start || !touch) return
    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_SLOPE) return
    onShift(dx < 0 ? 7 : -7)
  }

  return (
    <div className={s.week} onTouchStart={touchStart} onTouchEnd={touchEnd}>
      {cols.map(iso => {
        const on = new Date(`${iso}T12:00:00Z`)
        const dow = on.toLocaleDateString('en-GB', { weekday: 'short' })
        return (
          <button
            key={`head-${iso}`}
            type="button"
            className={[s.wh, iso === today ? s.today : '', iso < today ? s.gone : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onDay(iso)}
          >
            {/* The initial is for the eye at 47px; the full name is what is read out, and
                the stylesheet swaps which one is on screen at 721px. */}
            <span className={s.short} aria-hidden="true">
              {dow.slice(0, 1)}
            </span>
            <span className={s.long}>{dow}</span>
            <b>{on.getUTCDate()}</b>
          </button>
        )
      })}
      {cols.map(iso => {
        const due = !!pending?.(iso)
        return (
          <div
            key={`cell-${iso}`}
            className={iso < today ? `${s.wcell} ${s.gone}` : s.wcell}
            aria-busy={due || undefined}
          >
            {cell(nights.get(iso) ?? [], iso, room, onNights?.[iso] ?? 0, onDay)}
            {/* Under the cards already drawn, never over them, and without words. */}
            {due ? <span className={s.pending} aria-hidden="true" /> : null}
          </div>
        )
      })}
    </div>
  )
}

/**
 * One night's column: what fits, then a count. The count is a link into the day rather than
 * a number — nothing here is hidden, it is one click deeper.
 */
function cell(
  list: CalendarEntry[],
  iso: string,
  room: number,
  counted: number,
  onDay: (iso: string) => void,
) {
  /*
   * The night less the cards standing in the column, which is neither of the two numbers
   * this line held before.
   *
   * `list.length` is what the payload sent, and the cap makes that fewer than the night —
   * worse here than in the month, because a column's room is *deeper* than the cap, so
   * `list.length > room` is false down a whole payload and the control vanished on every
   * night rather than only the short cells.
   *
   * And what is subtracted is the cards drawn, not the room: with more room than rows the
   * column draws all of them, so counting off `room` reports two fewer than are missing on
   * every ordinary night.
   *
   * The larger of the count and the rows, because neither alone is a floor — `nights` counts
   * a run once under the night it opens while every night of the run draws it, and the four
   * personal tabs are handed no count at all. Same expression as `MonthGrid` and
   * `PhoneMonth`, for the same reason.
   */
  const drawn = Math.min(list.length, room)
  const hidden = Math.max(counted, list.length) - drawn
  return (
    <>
      {/* An `ol`, because the card is an `li` — the same list the front page's months are. */}
      <ol className={s.wlist}>
        {list.slice(0, room).map(e => (
          <WeekBlock key={e.id} entry={e} />
        ))}
      </ol>
      {hidden > 0 ? (
        <button type="button" className={s.wmore} onClick={() => onDay(iso)}>
          +{hidden}
        </button>
      ) : null}
    </>
  )
}

/**
 * One gig in a week column: the front page's card, in its narrow variant.
 *
 * It was its own thing — a chip background, a coloured rail down the left, its own type
 * scale — which is the third description of a card this site has had. `ListView` deleted
 * one of them by handing `CalendarEntry` straight to `EventCard`, and the note it left says
 * why that works: the entry is a structural superset of `LandingEvent`, so no adapter is
 * needed and both halves of the site read the same nine fields.
 *
 * The default variant, not `stack`. `stack` lays a card on its *side* — picture, words and
 * marks in three columns — which is the "Freshly curated" shape and needs width this column
 * has none of. The default is the front page's own: the poster on top, the name under it.
 *
 * The card brings its own marks from `MarksContext`, so the corner glyphs added here a
 * moment ago go with the rest of it.
 *
 * The friends as a line under the venue, not a stack on the poster. A week is seven
 * columns read across, and a poster in each with faces on it is seven rows of faces
 * competing with the artwork they sit on; the line says who beside the room, once, in the
 * type the rest of the column is set in.
 */
function WeekBlock({ entry }: { entry: CalendarEntry }) {
  return <EventCard event={entry} withDay={false} faces="line" />
}
